/* eslint-disable complexity */
/* eslint-disable max-lines-per-function */
import { useState } from 'react';
import { Avatar, AvatarImage, AvatarFallback } from '@e2n/ui';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { cn } from '@/lib/utils';
import { faCalendarStar, faXmark } from '@fortawesome/pro-regular-svg-icons';
import type { SizeProp } from '@fortawesome/fontawesome-svg-core';
import { markierungMapper } from '@/utils/markierung';
import { Button, Popover, PopoverTrigger, PopoverContent } from '@e2n/ui';
import { Variant } from '@/domain/termine/shared/types/TerminImage';
import { usePopoverDirection } from '@/domain/termine/shared/utils/getPopoverDirection';
import { MarkierungType } from '@/domain/termine/shared/types';

export interface TerminImageProps extends React.ComponentProps<typeof Avatar> {
  bildUrl?: string;
  bildName?: string;
  markierung?: MarkierungType;
  variant?: Variant;
  expandable?: boolean;
}

export function TerminImage({
  bildUrl,
  bildName,
  markierung,
  variant,
  expandable = true,
  ...avatarProps
}: TerminImageProps) {
  const [isOpen, setIsOpen] = useState(false);
  const sizeClasses: Record<string, string> = {
    detail: 'w-16 h-16',
    sheet: 'w-24 h-24',
    default: 'w-6 h-6',
  };
  const sizeClass =
    (variant ? sizeClasses[variant] : undefined) ?? sizeClasses.default;

  const iconSizes: Record<string, SizeProp> = {
    detail: '2x',
    sheet: '2x',
    default: 'sm',
  };
  const iconSize: SizeProp =
    (variant ? iconSizes[variant] : undefined) ?? iconSizes.default;

  const popoverDirection = usePopoverDirection();

  const imageKey = bildUrl ?? 'fallback';

  const avatar = (
    <Avatar
      {...avatarProps}
      key={imageKey}
      className={`${sizeClass} border-2 border-(--border-color) cursor-pointer ${
        avatarProps.className ?? ''
      }`}
      style={{
        borderColor: markierung ? markierungMapper[markierung].base : '',
        ...avatarProps.style,
      }}
      onClick={() => {
        if (expandable && bildUrl) setIsOpen(true);
      }}>
      <AvatarImage src={bildUrl} alt={bildName} className="object-cover" />
      <AvatarFallback
        className="bg-transparent"
        onClick={(e) => e.stopPropagation()}>
        <FontAwesomeIcon
          icon={faCalendarStar}
          size={iconSize}
          className="text-neutrals-800"
        />
      </AvatarFallback>
    </Avatar>
  );
  if (variant === 'list') return avatar;

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>{avatar}</PopoverTrigger>
      <PopoverContent
        side={popoverDirection}
        align="center"
        collisionPadding={8}
        sticky="partial"
        className={cn(
          'z-50 w-auto',
          'max-w-(--radix-popover-content-available-width)',
          'max-h-(--radix-popover-content-available-height)',
          'bg-white rounded-xl shadow-lg p-8 flex relative items-center justify-center',
        )}>
        <img
          onClick={(e) => e.stopPropagation()}
          src={bildUrl}
          alt={bildName}
          className="rounded-xl object-contain max-w-[40vw] max-h-[40dvh]"
        />
        <Button
          variant="ghost"
          onClick={() => setIsOpen(false)}
          aria-label="Close"
          className="absolute top-1 right-1 hover:bg-transparent z-20">
          <FontAwesomeIcon icon={faXmark} size="lg" />
        </Button>
      </PopoverContent>
    </Popover>
  );
}
